/**
 * OKR provenance — the CHAP-417 divergence guard.
 *
 * WHY THIS EXISTS
 *
 * Omega Zeta's campaign OKRs live in TWO stores at once during the campaign,
 * by Jake's explicit "both for now" decision (2026-08-03):
 *
 *   1. ChapterBuild / Postgres — written through `routes/okrs.ts` (CHAP-378)
 *      behind `requireAuth + requireTier('cabinet')`, with RLS and an
 *      `audit_events` row naming the acting user on every write.
 *   2. The legacy board dashboard / Cloudflare D1 — written through a Worker
 *      whose `isAuthorised()` compares one shared bearer token and has no
 *      concept of a user at all.
 *
 * There is NO sync between them. Two independently writable copies will
 * diverge the first time two people edit in different places, and neither
 * store will know it happened.
 *
 * The mitigation this module implements is option 2 of the plan's ordered
 * list: since both surfaces stay writable, each one must SAY which store it
 * reads and how fresh that copy is, so a reader can never mistake one copy for
 * the whole truth. That disclosure is the entire point — it is not decoration,
 * and removing it re-creates exactly the silent-drift defect CHAP-386 exists to
 * prevent.
 *
 * This module is deliberately pure (no React, no fetch) so the wording and the
 * freshness arithmetic are unit-testable. See `okr-provenance.test.ts`.
 */
import type { OkrInitiative } from '../../shared/types.js';

export type OkrStoreId = 'chapterbuild-postgres' | 'omega-club-d1';

export interface OkrStoreDescriptor {
  id: OkrStoreId;
  /** Full name, for the provenance line. */
  label: string;
  /** Short name, for inline prose. */
  shortLabel: string;
  /** Where a human goes to edit this copy. */
  editedAt: string;
  /**
   * Whether a write to this store can be traced to a person. Only the
   * ChapterBuild store can — this asymmetry is the reason CHAP-378 was built,
   * and it is why the legacy copy must never be presented as equivalent.
   */
  attributed: boolean;
}

export const CHAPTERBUILD_OKR_STORE: OkrStoreDescriptor = {
  id: 'chapterbuild-postgres',
  label: 'ChapterBuild (Postgres)',
  shortLabel: 'ChapterBuild',
  editedAt: 'this page',
  attributed: true,
};

export const LEGACY_BOARD_OKR_STORE: OkrStoreDescriptor = {
  id: 'omega-club-d1',
  label: 'board dashboard (Cloudflare D1)',
  shortLabel: 'the board dashboard',
  editedAt: 'the board dashboard OKR tracker',
  attributed: false,
};

/** Parse to epoch ms, or null if the value is absent or not a real date. */
function toEpoch(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * The most recent write across every initiative AND every key result.
 *
 * Key results are included deliberately: the common edit during a campaign is
 * nudging a key result's `current`, which touches only the child row. Reading
 * initiative timestamps alone would report a stale "last updated" and make an
 * actively-edited copy look abandoned.
 *
 * Returns the original ISO string (not a Date) so callers can render it
 * verbatim in a `dateTime` attribute.
 */
export function latestOkrUpdate(initiatives: readonly OkrInitiative[]): string | null {
  let bestIso: string | null = null;
  let bestMs = -Infinity;

  for (const init of initiatives) {
    const candidates: (string | null)[] = [init.updatedAt ?? null];
    for (const kr of init.keyResults ?? []) {
      candidates.push(kr.updatedAt ?? null);
    }
    for (const iso of candidates) {
      const ms = toEpoch(iso);
      if (ms !== null && ms > bestMs) {
        bestMs = ms;
        bestIso = iso;
      }
    }
  }

  return bestIso;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

/**
 * Human-readable age of a copy. Relative inside a week (the campaign cadence,
 * where "3 days ago" is the meaningful signal), absolute beyond it (where an
 * exact date makes a stale mirror unmistakable).
 *
 * `never` covers both "no timestamp" and "unparseable", because the honest
 * statement in both cases is that we cannot show when this copy last changed —
 * printing `Invalid Date` to a board member would be worse than useless.
 */
export function formatLastUpdated(iso: string | null, now: Date = new Date()): string {
  const ms = toEpoch(iso);
  if (ms === null) return 'never';

  // Clamp clock skew: a future timestamp reads as current, never as "-3 min ago".
  const age = Math.max(0, now.getTime() - ms);

  if (age < MINUTE) return 'just now';
  if (age < HOUR) {
    const mins = Math.floor(age / MINUTE);
    return `${mins} min ago`;
  }
  if (age < DAY) {
    const hours = Math.floor(age / HOUR);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  if (age < WEEK) {
    const days = Math.floor(age / DAY);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * The one sentence a reader of either surface must see. Names both copies and
 * states the absence of sync explicitly — a reader who sees only "ChapterBuild"
 * would reasonably assume it is the single source of truth, which is the exact
 * misreading this guard exists to prevent.
 */
export function okrDivergenceNotice(): string {
  return (
    `These OKRs are stored in ${CHAPTERBUILD_OKR_STORE.shortLabel}. ` +
    `${LEGACY_BOARD_OKR_STORE.shortLabel} keeps a separate copy that is not synced with this one, ` +
    `so the two can differ until cutover.`
  );
}

/**
 * The sentence a cabinet member needs at the moment they choose WHERE to type.
 *
 * This is the divergence guard's active half: it does not merely disclose the
 * duplication, it gives a reason to prefer the store that can name who made a
 * change. Producing single-writer behaviour by incentive is better than a
 * feature flag, which would have to disable the ONLY attributable writer in
 * order to protect the one that authorises every edit with a single shared
 * token — making the audit trail worse to make the drift story simpler.
 *
 * Derived from the descriptors' `attributed` flags rather than hardcoded, so
 * the claim cannot outlive the fact.
 */
export function okrAttributionNotice(): string {
  const here = CHAPTERBUILD_OKR_STORE.attributed
    ? 'Edits here are recorded against your name.'
    : 'Edits here are not attributed to anyone.';
  const there = LEGACY_BOARD_OKR_STORE.attributed
    ? `Edits on ${LEGACY_BOARD_OKR_STORE.shortLabel} are also attributed.`
    : `Edits on ${LEGACY_BOARD_OKR_STORE.shortLabel} are not attributed to anyone.`;
  return `${here} ${there}`;
}
